import React from 'react'
import Row from 'muicss/lib/react/row'
import Col from 'muicss/lib/react/col'
import {
  grey500,
  grey900,
  deepOrange500,
  greenA700
} from 'material-ui-legacy/styles/colors'
import ContentAddCircleOutline from 'material-ui-legacy/svg-icons/content/add-circle-outline'

import { monetize, percentize } from '../../lib/formats'
import DisplayField from '../UI/DisplayField'
import ServiceSelector from '../Services/ServiceSelector'
import ValidatedTextInput from '../UI/ValidatedTextInput'

/*
Props:
- label: String, value label
- value: Number, dollar value
*/
const DollarValue = props => {
  const color = props.signal ? greenA700 : deepOrange500
  return (
    <Row>
      <Col
        lg-offset="4"
        lg="4"
        md="7"
        sm="8"
        style={{ textAlign: 'left', color: grey500 }}
      >
        {props.label}
      </Col>
      <Col lg="4" md="5" sm="4" style={{ textAlign: 'right', color: color }}>
        {monetize(props.value)}
      </Col>
    </Row>
  )
}

/*
Props:
- label: String, value label
- value: Number, percent value
*/
const PercentageValue = props => {
  const color = props.signal ? greenA700 : deepOrange500
  return (
    <Row>
      <Col
        lg-offset="4"
        lg="4"
        md="7"
        sm="8"
        style={{ textAlign: 'left', color: grey500 }}
      >
        {props.label}
      </Col>
      <Col lg="4" md="5" sm="4" style={{ textAlign: 'right', color: color }}>
        {percentize(props.value)}
      </Col>
    </Row>
  )
}

/*
Props:
- budget: Number, budget value
- budgetSignal: Boolean, true = positive
- estimatedCost: Number, estimatedCost value
- estimatedCostSignal: Boolean, true = positive
- targetMargin: Number, targetMargin percentage value
- targetMarginSignal: Boolean, true = positive
*/
const ProjectSummary = props => (
  <div style={{ margin: '0 20px 0 20px' }}>
    <DollarValue
      label="Budget"
      value={props.budget}
      signal={props.budgetSignal}
    />
    <DollarValue
      label="Est. cost"
      value={props.estimatedCost}
      signal={props.estimatedCostSignal}
    />
    <PercentageValue
      label="Target margin"
      value={props.targetMargin}
      signal={props.targetMarginSignal}
    />
  </div>
)

/*
Props
- totalIsExact: Boolean, self-explanatory
- totalCost, grossTotal: Number, dollar values
- totalMargin: Number, percentage value
NOTE: The row/column structure is a bit weird because it's meant to match
      LineItemInput as much as possible
*/
const GrossTotal = props => {
  const marginColor =
    props.marginColor ||
    (Number(props.totalMargin) > Number(props.targetMargin || 0)
      ? greenA700
      : deepOrange500)
  const exactSuffix = props.totalIsExact ? ' (approx.)' : ''
  const exactStar = props.totalIsExact ? '*' : ''
  return (
    <main className="line-item-input">
      <div className="input-area grand-totals">
        <section className="main-container">
          <div className="row">
            <h3 className="xlarge">Gross Total</h3>
            <div className="tiny">
              <DisplayField
                name="totalCost"
                value={monetize(props.totalCost) + exactStar}
                floatingLabelText={'Cost' + exactSuffix}
                alignRight
              />
            </div>
            <div className="tiny">
              <DisplayField
                name="totalMargin"
                value={percentize(props.totalMargin) + exactStar}
                floatingLabelText={'Margin' + exactSuffix}
                color={marginColor}
                alignRight
              />
            </div>
          </div>
        </section>

        <section className="total-container">
          <div className="total">
            <DisplayField
              name="grossTotal"
              value={monetize(props.grossTotal)}
              floatingLabelText="Total"
              alignRight
            />
          </div>
          <div className="delete" />
        </section>
      </div>
    </main>
  )
}

/*
Props
- grandTotal: Number, dollar value
NOTE: The row/column structure is a bit weird because it's meant to match
      LineItemInput as much as possible
*/
const GrandTotal = props => (
  <main className="line-item-input">
    <div className="input-area grand-totals">
      <section className="main-container">
        <h3>Grand Total</h3>
      </section>
      <section className="total-container">
        <div className="total">
          <DisplayField
            name="grandTotal"
            value={monetize(props.grandTotal)}
            floatingLabelText="Total"
            alignRight
          />
        </div>
        <div className="delete" />
      </section>
    </div>
  </main>
)

/*
Props:
- taxCode: String, tax code/name
- taxRate: Number, tax percentage
- taxTotal: Number, display field for total tax dollar value
- validatedOnChange: Function(value, valid, attribute),
                     callback for change event
*/
const TaxInput = props => (
  <main className="line-item-input">
    <div className="input-area grand-totals">
      <section className="main-container">
        <div className="row">
          <h3 className="xlarge">Taxes</h3>
          <div className="tiny">
            <ValidatedTextInput
              name="taxCode"
              value={props.taxCode}
              floatingLabelText="Tax code"
              validatedOnChange={props.validatedOnChange}
              fullWidth
            />
          </div>
          <div className="tiny">
            <ValidatedTextInput
              name="taxRate"
              value={props.taxRate}
              floatingLabelText={'Tax rate'}
              validatedOnChange={props.validatedOnChange}
              pattern={/^-?(\d+)\.?(\d+)?$/}
              errorMessage="Invalid"
              fullWidth
              alignRight
              immediateValidation
            />
          </div>
        </div>
      </section>

      <section className="total-container">
        <div className="total">
          <DisplayField
            name="taxTotal"
            value={monetize(props.taxTotal)}
            floatingLabelText="Tax total"
            alignRight
          />
        </div>
        <div className="delete" />
      </section>
    </div>
  </main>
)

/*
Props:
- category: a category object
- selectedIdCallback: callback function to add a service for said category
*/
const ServiceCategoryHeader = props => {
  const category = props.category.attributes
  const categoryId = props.category.id
  return (
    <main className="service-category-header">
      <section className="category-label">{category.name}:</section>
      <section className="category-selector">
        <ServiceSelector
          categoryId={categoryId}
          placeholder={
            <span>
              <ContentAddCircleOutline style={{ margin: '0 3px -6px 0' }} />
              <strong>{category.name}</strong> line item...
            </span>
          }
          onSelected={props.onSelected}
          iconStyle={{ fill: grey900 }}
          resetOnSelected
          noUnderline
          fullWidth
        />
      </section>
    </main>
  )
}

/*
Props:
- service: a service resource object
*/
const ServiceHeader = ({ service }) => {
  const { name, formattedRate, formattedCost } = service.attributes

  return (
    <h4 className="service-header">
      <strong>{name}</strong>{' '}
      <span className="rate-and-cost">
        <em>Rate:</em> {formattedRate}
      </span>
      <span className="rate-and-cost">
        <em>Cost:</em> {formattedCost}
      </span>
    </h4>
  )
}

/* Props:
total: category total
NOTE: the structure is a bit weird to match LineItemInput.
*/
const ServiceCategorySubtotal = props => (
  <main className="line-item-input">
    <div className="input-area">
      <section className="main-container" />
      <section className="total-container">
        <div className="total">
          <DisplayField
            name="total"
            value={monetize(props.total)}
            floatingLabelText="Subtotal"
            alignRight
          />
        </div>
        <div className="delete" />
      </section>
    </div>
  </main>
)

export {
  DollarValue,
  PercentageValue,
  ProjectSummary,
  GrossTotal,
  GrandTotal,
  TaxInput,
  ServiceCategoryHeader,
  ServiceCategorySubtotal,
  ServiceHeader
}



// WEBPACK FOOTER //
// ./src/components/Quotes/QuoteFormComponents.jsx